import { useQuery } from '@tanstack/react-query';
import type { AppPermission, MyAccessView } from '@threadline/contracts';
import { useAuth } from '@/components/auth/auth-context';
import { api } from '@/lib/api';

/**
 * What the signed-in account may do, as the roles table grants it.
 *
 * The account's rank still decides what the API answers; this is the layer the
 * console honours on top, so that a menu leading somewhere the person cannot go
 * is never drawn.
 *
 * The cache key carries who asked. Signing out clears the whole query cache, so
 * this would be correct either way - but a permission set is what the sidebar is
 * built from, and answering a second person with the first one's is the kind of
 * wrong that looks like the product working.
 */
export function useAccess() {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ['access', user?.id],
    queryFn: () => api.get<MyAccessView>('/roles/me'),
    // Nobody signed in has no permissions to fetch, and asking would only earn
    // a 401 on the sign-in screen.
    enabled: Boolean(user),
    staleTime: 5 * 60_000,
  });

  const permissions = query.data?.permissions ?? [];

  return {
    can: (permission: AppPermission) => permissions.includes(permission),
    permissions,
    roleLabel: query.data?.role?.label ?? null,
    isLoading: query.isLoading,
    /**
     * True once there is an answer to act on - or once there is nobody to ask
     * about, so a signed-out visitor is not left watching a placeholder while
     * the shell redirects them to sign in.
     */
    isReady: !user || query.isSuccess || query.isError,
  };
}
